import React, { useEffect, useRef, useState } from 'react';
import { API_URL, buildKioskAuthHeaders, getStoredAuthToken } from './api';
import './ReservationSettings.css';

const FIELDS = [
  ['openingHour', 'Heure d’ouverture', 0, 23, 'Heure entière, de 0 à 23.'],
  ['closingHour', 'Heure de fermeture', 1, 23, 'Après l’ouverture ; aucun créneau ne commence à cette heure.'],
  ['slotMinutes', 'Pas des créneaux (minutes)', 15, 120, 'Intervalle entre deux départs de réservation.'],
  ['maxPartySize', 'Maximum de couverts par réservation', 1, 30, 'Limite pour un seul groupe.'],
  ['tableCapacity', 'Capacité en couverts', 1, 300, 'Couverts disponibles pour les réservations qui se chevauchent.'],
  ['defaultDurationMinutes', 'Durée moyenne d’une table (minutes)', 15, 360, 'Durée utilisée pour calculer les créneaux disponibles et les couverts occupés par les réservations qui se chevauchent.'],
];

function validSettings(settings) {
  return settings && FIELDS.every(([key, , min, max]) => Number.isInteger(settings[key]) && settings[key] >= min && settings[key] <= max)
    && settings.closingHour > settings.openingHour && settings.tableCapacity >= settings.maxPartySize && settings.defaultDurationMinutes >= settings.slotMinutes;
}

function parsePayload(payload) {
  if (payload?.schemaVersion !== 1 || !/^[a-f0-9]{64}$/.test(payload.revision || '') || !validSettings(payload.settings)) {
    throw new Error('Les paramètres reçus sont invalides. Rechargez-les avant de modifier.');
  }
  return { revision: payload.revision, settings: Object.fromEntries(FIELDS.map(([key]) => [key, payload.settings[key]])) };
}

const SESSION_MESSAGE = 'Votre session a changé. Rechargez la page avant de modifier les paramètres.';

export default function ReservationSettings({ allowed = false }) {
  const [open, setOpen] = useState(false);
  const [baseline, setBaseline] = useState(null);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [conflict, setConflict] = useState(false);
  const [sessionChanged, setSessionChanged] = useState(false);
  const [confirmReload, setConfirmReload] = useState(false);
  const ownerToken = useRef(null), request = useRef(0), controller = useRef(null), heading = useRef(null), allowedRef = useRef(allowed);
  allowedRef.current = allowed;
  const dirty = Boolean(draft && baseline && FIELDS.some(([key]) => String(draft[key]) !== String(baseline.settings[key])));

  useEffect(() => () => { request.current += 1; controller.current?.abort(); }, []);
  useEffect(() => { if (open) heading.current?.focus(); }, [open]);

  function checkSession() {
    if (!ownerToken.current || getStoredAuthToken() !== ownerToken.current) {
      setSessionChanged(true);
      throw new Error(SESSION_MESSAGE);
    }
  }

  async function transact(method, settings) {
    let sequence;
    let sent = false, definitiveFailure = false;
    try {
      checkSession();
      if (!allowedRef.current) throw new Error('Votre accès ne permet plus de modifier ces paramètres.');
      sequence = ++request.current;
      controller.current?.abort();
      controller.current = new AbortController();
      setBusy(true); setError(''); setMessage(''); setConfirmReload(false);
      const headers = buildKioskAuthHeaders();
      if (method === 'PUT') headers.set('Content-Type', 'application/json');
      sent = true;
      const response = await fetch(`${API_URL}/api/caisse/reservation-settings`, {
        method, headers, cache: 'no-store', signal: controller.current.signal,
        ...(method === 'PUT' ? { body: JSON.stringify({ revision: baseline.revision, settings }) } : {}),
      });
      const payload = await response.json().catch(() => ({}));
      if (sequence !== request.current) return;
      checkSession();
      if (!allowedRef.current) throw new Error('Votre accès ne permet plus de modifier ces paramètres.');
      if (!response.ok) {
        definitiveFailure = response.status >= 400 && response.status < 500;
        if (response.status === 401 || response.status === 403) {
          setSessionChanged(true);
          throw new Error('Votre accès aux paramètres a été refusé. Votre saisie est conservée. Rechargez la page pour vérifier votre session.');
        }
        if (response.status === 409 && payload.code === 'RESERVATION_SETTINGS_CONFLICT') {
          setConflict(true);
          throw new Error('Les paramètres ont été modifiés ailleurs. Votre saisie est conservée. Rechargez les paramètres avant de les modifier à nouveau.');
        }
        throw new Error(typeof payload.error === 'string' ? payload.error : 'Impossible de vérifier les paramètres. Votre saisie est conservée.');
      }
      const next = parsePayload(payload);
      setBaseline(next); setDraft(next.settings); setConflict(false);
      setMessage(method === 'PUT' ? 'Paramètres enregistrés.' : 'Paramètres actualisés.');
    } catch (failure) {
      if (sequence !== undefined && sequence !== request.current) return;
      if (method === 'PUT' && sent && !definitiveFailure) setConflict(true);
      setError(failure instanceof TypeError ? 'Réponse non reçue. Votre saisie est conservée. Rechargez les paramètres pour vérifier leur état avant un nouvel enregistrement.' : failure.message || 'Impossible de vérifier les paramètres. Votre saisie est conservée.');
    } finally {
      if (sequence === undefined || sequence === request.current) setBusy(false);
    }
  }

  function show() {
    setOpen(true);
    if (ownerToken.current === null) ownerToken.current = getStoredAuthToken();
    if (!baseline) transact('GET');
  }

  function save(event) {
    event.preventDefault();
    if (busy || conflict || sessionChanged || !allowed || !baseline) return;
    const settings = Object.fromEntries(FIELDS.map(([key]) => [key, draft[key] === '' ? NaN : Number(draft[key])]));
    if (!validSettings(settings)) { setError('Vérifiez les nombres entiers : fermeture après ouverture, capacité au moins égale au groupe et durée au moins égale au pas des créneaux.'); return; }
    transact('PUT', settings);
  }

  if (!open) return allowed ? <button type="button" className="bc-resa-btn bc-resa-btn-secondary reservation-settings-open" onClick={show}>Paramètres des réservations</button> : null;

  return <section className="reservation-settings" aria-labelledby="reservation-settings-title">
    <div className="reservation-settings-head"><h2 id="reservation-settings-title" ref={heading} tabIndex={-1}>Paramètres des réservations</h2><button type="button" className="bc-resa-btn bc-resa-btn-secondary" disabled={busy} onClick={() => setOpen(false)}>Fermer les paramètres</button></div>
    <p>Ces réglages s’appliquent aux réservations de cet établissement. La plage horaire reste dans une même journée ; aucune table physique n’est affectée ici.</p>
    {!allowed && <p role="alert">Votre accès ne permet plus de modifier ces paramètres. Votre saisie est conservée.</p>}
    {error && <p className="reservation-settings-error" role="alert">{error}</p>}
    {message && <p role="status">{message}</p>}
    {busy && <p role="status">Vérification des paramètres…</p>}
    {draft && <form onSubmit={save}>
      <fieldset disabled={busy || !allowed || sessionChanged}>
        <legend className="sr-only">Réglages des réservations</legend>
        <div className="reservation-settings-grid">{FIELDS.map(([key, label, min, max, help]) => <label key={key}><span>{label}</span><input aria-label={label} aria-describedby={`reservation-setting-${key}-help`} type="number" required min={min} max={max} step={1} value={draft[key]} onChange={event => { setDraft(current => ({ ...current, [key]: event.target.value })); setMessage(''); setConfirmReload(false); }} /><small id={`reservation-setting-${key}-help`}>{help}</small></label>)}</div>
        <div className="reservation-settings-actions"><button type="submit" className="bc-resa-btn" disabled={!dirty || conflict}>Enregistrer les paramètres</button><button type="button" className="bc-resa-btn bc-resa-btn-secondary" disabled={!dirty} onClick={() => { setDraft(baseline.settings); setMessage('Modifications locales annulées.'); setConfirmReload(false); }}>Annuler les modifications</button></div>
      </fieldset>
    </form>}
    <div className="reservation-settings-actions"><button type="button" className="bc-resa-btn bc-resa-btn-secondary" disabled={busy || !allowed || sessionChanged} onClick={() => dirty ? setConfirmReload(true) : transact('GET')}>Recharger les paramètres</button></div>
    {confirmReload && <div className="reservation-settings-confirm" role="group" aria-label="Confirmer le rechargement"><p>Recharger remplacera votre saisie par les paramètres enregistrés sur le serveur.</p><button type="button" className="bc-resa-btn bc-resa-btn-secondary" onClick={() => transact('GET')}>Recharger et remplacer ma saisie</button><button type="button" className="bc-resa-btn bc-resa-btn-secondary" onClick={() => setConfirmReload(false)}>Conserver ma saisie</button></div>}
  </section>;
}
